import { useActiveBookingContext, useAppState, useBookingEngineStateContext, useCustomNavigate, usePropertyContext } from "@app/app/hooks";
import { configManager } from "@app/app/services/configuration/ConfigManager";
import { tracker } from "@app/app/services/tracker";
import { IBookingEngineState } from "@app/app/types";
import { getRouteChildValue } from "@app/app/utils/appUtils";
import { BookableItem } from "@app/availability/components/BookableItem";
import { GuestSummary } from "@app/common/components/GuestSummary";
import { NoAvailabilityModal } from "@app/common/components/NoAvailabilityModal";
import { RoomFilterModal } from "@app/common/components/RoomFilterModal";
import { BookableType, TBookableItem } from "@app/common/types";
import { Button } from "@app/componentLibrary/ui/button";
import { Flex } from "@app/componentLibrary/ui/flex";
import { LoadingDots } from "@app/componentLibrary/ui/LoadingDots";
import { Text } from "@app/componentLibrary/ui/text";
import { ReviewComponent } from "@app/review/components/ReviewComponent";
import { format, parseISO } from "date-fns";
import { Fragment, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

import { SearchConfiguratorDialog } from "../components/SearchConfiguratorDialog";

export const AvailabilityOverviewPage = () => {
    const navigate = useCustomNavigate();
    const { activeProperty } = usePropertyContext();
    const { getState, isLoading, filteredBookableItems } = useBookingEngineStateContext();
    const { activeBookingIndex } = useActiveBookingContext();
    const { setValue } = useFormContext<IBookingEngineState>();
    const [isAvailabilityOverviewTracked, setIsAvailabilityOverviewTracked] = useState(false);
    const { bookingState } = getState();
    const { fromDate, toDate } = bookingState?.dates || {};
    const { t } = useTranslation(["name", "description", "availability", "common"]);
    const isSingleRateBooking = activeProperty?.single_rate_booking;
    const activeBooking = (bookingState?.bookings || [])[activeBookingIndex];
    const isMultiRoomBooking = bookingState?.bookings?.length > 1;
    const selectedRate = isMultiRoomBooking && activeBookingIndex > 0 ? bookingState.bookings[0].rate : null;
    const propertyName = configManager.isTestMode() ? "Hotel Sluys Grand" : activeProperty?.name || "";
    const [calendarAndFiltersBackground, setCalendarAndFiltersBackground] = useState("bg-white");
    const { registerVisitedPage } = useAppState();

    /**
    If the property supports only single rate bookings (ie all the bookings must use the same rate, due to channel restriction)
    We filter the next booking selection rates to the rate selected in the first booking
    */
    const bookableItems =
        isMultiRoomBooking && isSingleRateBooking && activeBookingIndex > 0
            ? filteredBookableItems
                  .filter((item) => {
                      const isRoom = item.bookable_type === BookableType.Room;
                      return isRoom
                          ? item.rates.some((r) => r.id === selectedRate?.id)
                          : item.rooms.some((room) => room.rates.some((r) => r.id === selectedRate?.id));
                  })
                  .map((item) => {
                      const isRoom = item.bookable_type === BookableType.Room;
                      return isRoom
                          ? { ...item, rates: item.rates.filter((r) => r.id === selectedRate?.id) }
                          : {
                                ...item,
                                rooms: item.rooms.map((room) => ({
                                    ...room,
                                    rates: (room.rates || []).filter((r) => r.id === selectedRate?.id),
                                })),
                            };
                  })
            : filteredBookableItems;

    const handleSelection = (item: TBookableItem) => {
        const key = item.bookable_type === BookableType.Room ? "room" : "package";

        if (activeBookingIndex !== -1) {
            setValue(`bookingState.bookings.${activeBookingIndex}.${key}`, item);
            setValue(`bookingState.bookings.${activeBookingIndex}.type`, item.bookable_type);
        }

        tracker.selectItem({
            itemListId: "availability_overview",
            itemListName: "Availability overview",
            bookableItems: [item],
        });

        navigate("?p=base&c=item");
    };

    const handleOnMouseOverCalendarDateSelection = () => {
        setCalendarAndFiltersBackground("bg-gray-50");
    };

    const handleOnMouseOutCalendarDateSelection = () => {
        setCalendarAndFiltersBackground("bg-white");
    };

    useEffect(() => registerVisitedPage({ url: document.location.href, page: getRouteChildValue() || "" }), []);

    useEffect(() => {
        if (!isAvailabilityOverviewTracked && !isLoading && !!bookableItems.length) {
            setIsAvailabilityOverviewTracked(true);

            tracker.viewItemList({ itemListId: "availability_overview", itemListName: "Availability overview", bookableItems });
        }
    }, [bookableItems, isLoading, isAvailabilityOverviewTracked]);

    // Reset the booking selections
    useEffect(() => {
        if (activeBookingIndex !== -1) {
            setValue(`bookingState.bookings.${activeBookingIndex}.package`, null);
            setValue(`bookingState.bookings.${activeBookingIndex}.room`, null);
            setValue(`bookingState.bookings.${activeBookingIndex}.rate`, null);
            setValue(`bookingState.bookings.${activeBookingIndex}.extras`, []);
        }
    }, [activeBookingIndex, setValue]);

    return (
        <Fragment>
            {isMultiRoomBooking && (
                <Flex
                    alignItems="center"
                    justifyContent="center"
                    className="bg-[#E3E3E3] backdrop-blur-sm bg-opacity-90 border-t border-gray-200 text-gray-900 text-sm font-medium rounded-bl-xl rounded-br-xl bottom-0 absolute z-[19] w-full px-5 py-2 "
                >
                    <div data-testid="multi-room-overlay">
                        {t("choosingRoom", { ns: "common" })} {(activeBookingIndex !== -1 ? activeBookingIndex : 0) + 1}/
                        {bookingState?.bookings?.length}
                    </div>
                </Flex>
            )}

            <div className="grow overflow-auto px-5 pb-8 pt-6 w-full h-full">
                <Flex content="start" className={twMerge("flex-none mb-8 border rounded-xl", calendarAndFiltersBackground)}>
                    <NoAvailabilityModal />

                    <Flex justifyContent="start" className="p-4">
                        <SearchConfiguratorDialog
                            triggerButton={
                                <Button
                                    variant={"ghost"}
                                    className="w-full justify-start hover:bg-transparent cursor-pointer p-0"
                                    onMouseOver={handleOnMouseOverCalendarDateSelection}
                                    onMouseOut={handleOnMouseOutCalendarDateSelection}
                                >
                                    <Flex flexDirection="col" alignItems="start" className="m-1">
                                        {!!bookingState?.dates && (
                                            <div data-testid="availability-guests">
                                                <GuestSummary
                                                    adultsCount={activeBooking?.adultsCount || 0}
                                                    childCount={activeBooking?.childCount}
                                                />
                                            </div>
                                        )}
                                        {fromDate && toDate && (
                                            <div data-testid="selected-dates">
                                                <Text className="text-sm font-normal text-gray-400">
                                                    {format(parseISO(fromDate), "MMM. dd")} - {format(parseISO(toDate), "MMM. dd")},{" "}
                                                    {format(parseISO(toDate), "yyyy")}
                                                </Text>
                                            </div>
                                        )}
                                    </Flex>
                                </Button>
                            }
                        />
                    </Flex>

                    <RoomFilterModal triggerButtonClassName="mr-4" />
                </Flex>
                {isLoading ? (
                    <LoadingDots />
                ) : (
                    <Fragment>
                        <ReviewComponent propertyName={propertyName} />

                        <ul data-testid="bookable-items" className="gap-y-8 w-full grid grid-cols-1" color="white">
                            {(bookableItems || [])
                                .filter((item) => item.images.length > 0)
                                .map((item) => (
                                    <BookableItem item={item} onClick={() => handleSelection(item)} />
                                ))}
                        </ul>

                        {isMultiRoomBooking && <div className="h-8" />}
                    </Fragment>
                )}
            </div>
        </Fragment>
    );
};
